import { useEffect, useState } from "react";
import { Label, getLabelById, labelImageUrl, peekLabelById } from "@/lib/labels";

/**
 * Get label cover image URL for use as placeholder
 * @param label - The label object
 * @returns The label cover image URL or null
 */
export function getLabelPlaceholderImageUrl(
  label: Label | null
): string | null {
  if (!label?.cover_image) {
    return null;
  }

  // Check if it's a valid URL or data URI
  if (
    label.cover_image.startsWith("http") ||
    label.cover_image.startsWith("data:")
  ) {
    return label.cover_image;
  }

  return null;
}

/**
 * Check if a label has a valid cover image
 * @param label - The label object
 * @returns Boolean indicating if label has a valid cover image
 */
export function hasLabelPlaceholderImage(label: Label | null): boolean {
  return Boolean(getLabelPlaceholderImageUrl(label));
}

/**
 * Get the best available placeholder image URL
 * Priority: label cover image > default placeholder
 * @param label - The label object
 * @param defaultPlaceholder - Default placeholder URL to use as fallback
 * @returns The best available placeholder image URL
 */
export function getBestPlaceholderImageUrl(
  label: Label | null,
  defaultPlaceholder?: string
): string {
  const labelImage = getLabelPlaceholderImageUrl(label);
  if (labelImage) {
    return labelImage;
  }

  return defaultPlaceholder || "/placeholder.svg";
}

/**
 * Create a placeholder image component props object with label fallback
 * @param label - The label object
 * @param defaultPlaceholder - Default placeholder URL
 * @param className - Additional CSS classes
 * @returns Props object for img element with error handling
 */
export function getPlaceholderImageProps(
  label: Label | null,
  defaultPlaceholder?: string,
  className?: string
) {
  const labelImage = getLabelPlaceholderImageUrl(label);
  const fallbackImage = defaultPlaceholder || "/placeholder.svg";

  return {
    src: labelImage || fallbackImage,
    className,
    onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
      const target = e.target as HTMLImageElement;
      // If we're showing label image and it fails, fall back to default
      if (target.src === labelImage && fallbackImage) {
        target.src = fallbackImage;
      } else {
        // Hide the image if default also fails
        target.style.display = "none";
      }
    },
  };
}

/**
 * The cover image of a SPECIFIC label, for use as artwork fallback.
 *
 * `getLabelPlaceholderImageUrl` above resolves the VIEWER's own label, which is why the artwork
 * fallback only ever worked for sellers: a seller's tracks are all their own label, while a buyer
 * has no label at all and browses many. This resolves the label the TRACK belongs to, so the
 * fallback works the same for both.
 *
 * `getLabelById` is cached and de-duplicates in-flight requests, so a grid of 30 cards makes one
 * request per distinct label, not one per card.
 *
 * Returns null while loading and when the label has no cover — the caller then shows its own
 * default. It deliberately never falls back to the VIEWER's label: showing the buyer's own logo,
 * or another company's, on someone else's track would be worse than showing nothing.
 *
 * It starts from a label `getLabelById` has already loaded (`peekLabelById`), so a page that
 * loads its labels before it paints (the home page) draws the logo in its first frame instead of
 * swapping it in after this effect.
 */
export function useLabelCoverImage(labelTag?: string | null): string | null {
  const [url, setUrl] = useState<string | null>(() => {
    const tag = (labelTag || "").trim();
    return tag ? labelImageUrl(peekLabelById(tag)?.cover_image) : null;
  });

  useEffect(() => {
    const tag = (labelTag || "").trim();
    if (!tag) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    getLabelById(tag)
      .then((result) => {
        if (cancelled) return;
        setUrl(result.success ? labelImageUrl(result.data?.cover_image) : null);
      })
      .catch(() => {
        // An artwork fallback must never surface an error — it degrades to the default art.
        if (!cancelled) setUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [labelTag]);

  return url;
}
